// ============================================================================
// erreursFront.js — Journal des erreurs front (lot Fiabilité 4, 2026-07-18)
// ============================================================================
// PASSIF par construction : n'altère jamais le comportement de l'app.
// Capture et journalise dans la collection `erreursFront` (append-only) :
//   • les erreurs de rendu React (via ErrorBoundaryAramis) ;
//   • les erreurs JS non rattrapées (window.onerror) ;
//   • les promesses rejetées non gérées (unhandledrejection).
// Garde-fous : dédoublonnage par empreinte, plafond de 10 écritures par
// session, URL expurgée de sa query string (tokens !), écriture best-effort
// (un échec de log ne remonte jamais), rien en mode développement.

const _dejaVues = new Set();
let _restant = 10;

function _empreinte(s) {
  // djb2 — suffisant pour regrouper des erreurs identiques
  let h = 5381;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

export async function logErreurFront({ message, stack, source, composant }) {
  try {
    if (process.env.NODE_ENV === "development") return;
    const msg = String(message || "erreur inconnue").slice(0, 500);
    const stk = String(stack || "").slice(0, 2000);
    const emp = _empreinte(msg + "|" + stk.slice(0, 300));
    if (_dejaVues.has(emp) || _restant <= 0) return;
    _dejaVues.add(emp);
    _restant--;
    const { getFirestore, collection, addDoc } = await import("firebase/firestore");
    const { db } = await import("./firebase");
    await addDoc(collection(db || getFirestore(), "erreursFront"), {
      ts: Date.now(),
      message: msg,
      stack: stk,
      source: String(source || "js").slice(0, 30),
      composant: String(composant || "").slice(0, 200),
      // Jamais la query string : les liens Aramis portent des tokens.
      url: String(window.location.origin + window.location.pathname).slice(0, 300),
      vue: String(new URLSearchParams(window.location.search).get("view") || "").slice(0, 60),
      userAgent: String(navigator.userAgent || "").slice(0, 200),
      ecran: `${window.innerWidth}x${window.innerHeight}`,
      empreinte: emp,
    });
  } catch (_) { /* le journal ne doit jamais faire de bruit */ }
}

let _installe = false;
// Échec d'une écriture d'ARCHIVE — historique versionné d'un registre RGPD,
// preuve de formation. L'action principale a réussi et ne doit pas échouer
// pour autant ; mais l'échec ne doit plus jamais être silencieux. Incident du
// 22/09/2026 : pendant des mois, les règles Firestore refusaient ces écritures
// et un simple console.warn l'avalait — aucun historique de demande de droits,
// de violation ni de sous-traitant n'a été conservé, sans que personne le sache.
// L'échec remonte désormais dans le panneau des erreurs (collection erreursFront).
export function signalerEchecArchive(etiquette, e) {
  console.warn(`[${etiquette}] :`, e?.message || e);
  logErreurFront({
    message: `Archive non enregistrée — ${etiquette} : ${e?.message || e}`,
    stack: e?.stack || "", source: "archive", composant: etiquette,
  });
}

export function initErreursFront() {
  if (_installe || typeof window === "undefined") return;
  _installe = true;
  window.addEventListener("error", (ev) => {
    // Erreurs de chargement de ressources (img, script) : ev.error absent → ignore
    if (!ev || !ev.error) return;
    logErreurFront({ message: ev.error.message || ev.message, stack: ev.error.stack, source: "window" });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const r = ev && ev.reason;
    logErreurFront({
      message: (r && (r.message || String(r))) || "promesse rejetée",
      stack: r && r.stack,
      source: "promise",
    });
  });
}
